import React, { useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { useLocalStorage, useSessionStorage } from 'react-use';
import useIsChartCoverageDemo from '../hooks/useIsChartCoverageDemo';
import useIsBeta from '../hooks/useIsBeta';
import useMeasureElevation from '../hooks/elevation/useMeasureElevation';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
const baseStyleUrl = import.meta.env.VITE_STYLE_URL;


const MapContext = React.createContext(null);

export default MapContext;

const encodeGetParams = p => 
    Object.entries(p).map(kv => kv.map(encodeURIComponent).join("=")).join("&");

function buildChartStyleParams({
    style,
    styleSettings,
    depthUnits,
    isChartCoverageDemo,
    hybrid,
    showLightSectors,
    showUserReports,
    showMaritimeBoundaries,
    showRasterSurface,
}) {
    const rasterMode = showRasterSurface === 'false' ? null : showRasterSurface;
    return {
        theme: style,
        showEncBoundaries: isChartCoverageDemo || styleSettings.showEncBoundaries,
        depthLimit: styleSettings.depthLimit,
        depthUnits: depthUnits,
        isChartCoverageDemo: isChartCoverageDemo,
        showNoData: styleSettings.showNoData,
        showPlaceNames: true,
        showOverscale: false,
        featurePreview: true,
        showSatellite: hybrid,
        showLightSectors: showLightSectors,
        showUserReports: showUserReports,
        showMaritimeBoundaries: showMaritimeBoundaries,
        showRasterDepth: rasterMode === 'depth' || rasterMode === 'both',
        showRasterHillshade: rasterMode === 'hillshade' || rasterMode === 'both',
        font: 'Open Sans Regular',
        // hideLayers: "bcncar,bcnisd,bcnlat,bcnsaw,bcnspp,boycar,boyinb,boyisd,boylat,boysaw,boyspp,lights,wrecks,lndmrk"
    };
}

export function MapContextProvider({ children }) {
    const mapRef = useRef(null);
    const rulerControlRef = useRef(null);
    const [raster, setRaster] = useLocalStorage('chart-raster', false);
    const [style, setStyle] = useLocalStorage('chart-style', 'day');
    const [hybrid, setHybrid] = useLocalStorage('chart-hybrid', false);
    const [showLightSectors, setShowLightSectors] = useLocalStorage('chart-show-light-sectors', true);
    const [showUserReports, setShowUserReports] = useLocalStorage('chart-show-user-reports', false);
    const [showMaritimeBoundaries, setShowMaritimeBoundaries] = useSessionStorage(
        'chart-show-maritime-boundaries',
        'false',
    );
    const [maritimeBoundariesMode, setMaritimeBoundariesMode] = useSessionStorage(
        'chart-maritime-boundaries-mode',
        'lines',
    );
    // 'false' | 'depth' | 'hillshade' | 'both' — mirrors maritime boundaries pattern
    const [showRasterSurfacePreference, setShowRasterSurface] = useLocalStorage(
        'chart-show-raster-surface',
        'false',
    );
    const [rasterSurfaceMode, setRasterSurfaceMode] = useLocalStorage(
        'chart-raster-surface-mode',
        'depth',
    );
    const [showTerrain3dPreference, setShowTerrain3d] = useLocalStorage(
        'chart-show-terrain-3d',
        false,
    );
    const [terrainExaggeration, setTerrainExaggeration] = useLocalStorage(
        'chart-terrain-exaggeration',
        1.0,
    );
    const [depthUnits, setDepthUnits] = useLocalStorage('depth-units', 'meters');
    const [measureState, setMeasureState] = useState(null);
    const [elevationGraphVisible, setElevationGraphVisible] = useLocalStorage(
        'show-elevation',
        true,
    );
    const elevationState = useMeasureElevation({
        coordinates: measureState?.coordinates || [],
        enabled: !!measureState && !!elevationGraphVisible,
    });
    const [markers, setMarkers] = useState([]);
    const [reportIssueState, setReportIssueState] = useState(null);
    const isChartCoverageDemo = useIsChartCoverageDemo();
    const isBeta = useIsBeta();
    const showRasterSurface = isBeta ? showRasterSurfacePreference : 'false';
    const showTerrain3d = isBeta && !!showTerrain3dPreference;
    const [styleSettings, setStyleSettings] = useState({
        showEncBoundaries: false || isChartCoverageDemo,
        showTileBoundaries: false,
        useLocal: false,
        showNoData: true,
        depthLimit: 3.0,
    });
    const [contextMenuLocation, setContextMenuLocation] = useState(null);

    const chartStyleParams = buildChartStyleParams({
        style,
        styleSettings,
        depthUnits,
        isChartCoverageDemo,
        hybrid,
        showLightSectors,
        showUserReports,
        showMaritimeBoundaries,
        showRasterSurface,
    });

    const activeBaseStyleUrl = raster
        ? baseStyleUrl.replace('base.json', 'raster.json')
        : baseStyleUrl;
    const styleUrl = `${activeBaseStyleUrl}&` + encodeGetParams(chartStyleParams);
    
    const getTileCoordsForPosition = (zoom, lat, lon) => {
        const lat_rad = (lat) * (Math.PI / 180.0);
        const n = 1 << zoom;
        const xtile = Math.floor((lon + 180.0) / 360.0 * n);
        const ytile = Math.floor((1.0 - Math.asinh(Math.tan(lat_rad)) / Math.PI) / 2.0 * n);
      
        return {
            z: zoom,
            x: xtile,
            y: ytile
        }
    }

    return (
        <MapContext.Provider
            value={{
                mapRef,
                style,
                setStyle,
                styleSettings,
                setStyleSettings,
                contextMenuLocation,
                setContextMenuLocation,
                markers,
                setMarkers,
                styleUrl,
                getTileCoordsForPosition,
                depthUnits,
                setDepthUnits,
                raster,
                setRaster,
                measureState,
                setMeasureState,
                rulerControlRef,
                elevationState,
                elevationGraphVisible,
                setElevationGraphVisible,
                reportIssueState,
                setReportIssueState,
                hybrid, setHybrid,
                showLightSectors, setShowLightSectors,
                showUserReports, setShowUserReports,
                showMaritimeBoundaries, setShowMaritimeBoundaries,
                maritimeBoundariesMode, setMaritimeBoundariesMode,
                showRasterSurface, setShowRasterSurface,
                rasterSurfaceMode, setRasterSurfaceMode,
                showTerrain3d, setShowTerrain3d,
                terrainExaggeration, setTerrainExaggeration,
            }}
        >
            {children}
        </MapContext.Provider>
    );
}
